import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { RouterOutlet } from '@aviv-unified-frontend/runtime/router';
import { Box } from '@gemini/core';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { EstimateBlock } from '@/components/EstimateBlock/EstimateBlock';
import MapComponent from '@/components/Map';
import { MapErrorContent } from '@/components/MapErrorContent';
import Searchbox from '@/components/Searchbox';
import { useMobileViewDetect } from '@/hooks/useMobileViewDetect/useMobileViewDetect';
import { usePageLayoutContext } from '@/hooks/usePageLayoutContext/usePageLayoutContext';
import { useSticky } from '@/hooks/useSticky/useSticky';
import { PageSpinner } from '../PageSpinner';
export default function Sidebar() {
    const { sidebarOpen, isLoading } = usePageLayoutContext();
    const { isMobileView } = useMobileViewDetect();
    const [distanceFromBottom, setDistanceFromBottom] = useState(0);
    const searchBoxRef = useRef(null);
    const estimateBlockRef = useRef(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [sideBarContentDomNode, setSideBarContentDomNode] = useState(null);
    const searchBoxIsSticky = useSticky({ ref: searchBoxRef, position: 'top' });
    const estimateBlockIsSticky = useSticky({ ref: estimateBlockRef, position: 'bottom' });
    const onContentRefChange = useCallback((node) => {
        setSideBarContentDomNode(node);
    }, []);
    useEffect(() => {
        window.addEventListener('load', () => {
            sessionStorage.removeItem('hasFiredSidePanelAgencyCardViewItemListTracking');
        });
        if (!sidebarOpen && isMobileView) {
            document.body.style.overflow = 'hidden';
        }
        else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [sidebarOpen, isMobileView]);
    useEffect(() => {
        const getDistanceFromBottom = () => {
            if (searchBoxRef.current) {
                const rect = searchBoxRef.current.getBoundingClientRect();
                const distance = window.innerHeight - rect.bottom;
                setDistanceFromBottom(distance);
            }
        };
        getDistanceFromBottom();
        window.addEventListener('resize', getDistanceFromBottom);
        window.addEventListener('scroll', getDistanceFromBottom);
        return () => {
            window.removeEventListener('resize', getDistanceFromBottom);
            window.removeEventListener('scroll', getDistanceFromBottom);
        };
    }, []);
    return (_jsxs(Box, { display: "flex", flexDirection: "column", width: {
            'breakpoint.base': '100%',
            'breakpoint.lg': '37.5%',
            'breakpoint.xl': '41.6666667%',
        }, justifyContent: "space-between", backgroundColor: "color.background.default", color: "color.content.default.default", position: "relative", id: "sidebar-component", height: isMobileView && !sidebarOpen ? 'calc(100vh - 4em)' : '100%', children: [isLoading && (_jsx(Box, { position: "absolute", top: "0", bottom: "0", left: "0", right: "0", backgroundColor: "color.background.light", zIndex: "zIndex.3", display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", opacity: "opacity.80", children: _jsx(PageSpinner, { flexDirection: "row" }) })), _jsx(Box, { zIndex: "zIndex.3", padding: "spacing.16", position: "sticky", boxShadow: searchBoxIsSticky ? 'shadow.16' : undefined, top: "0", backgroundColor: "color.background.default", borderBottomStyle: "solid", borderBottomWidth: searchBoxIsSticky ? undefined : 'borderWidth.1', borderBottomColor: "color.border.light.default", ref: searchBoxRef, children: _jsx(Searchbox, {}) }), _jsx(Box, { ref: onContentRefChange, display: "flex", flexDirection: "column", flexGrow: "1", position: "relative", height: "100%", children: _jsxs(Box, { position: "relative", id: "sidebar-content", flexGrow: "1", height: "100%", children: [_jsxs(Box, { backgroundColor: "color.background.default", display: "flex", flexDirection: "column", position: "relative", zIndex: sidebarOpen ? 'zIndex.2' : 'zIndex.1', children: [_jsx(Box, { flexGrow: "1", paddingHorizontal: {
                                        'breakpoint.base': 'spacing.16',
                                        'breakpoint.sm': 'spacing.32',
                                    }, children: _jsx(RouterOutlet, {}) }), _jsx(Box, { position: "sticky", boxShadow: estimateBlockIsSticky ? 'shadow.16' : undefined, backgroundColor: "color.background.default", bottom: "0", width: "100%", paddingHorizontal: {
                                        'breakpoint.base': 'spacing.16',
                                        'breakpoint.sm': 'spacing.32',
                                    }, paddingVertical: "spacing.24", ref: estimateBlockRef, children: _jsx(EstimateBlock, {}) })] }), _jsx(Box, { display: "flex", flexDirection: "column", position: "absolute", top: "0", width: "100%", height: isMobileView && !sidebarOpen ? `${distanceFromBottom}px` : '100%', zIndex: sidebarOpen ? 'zIndex.1' : 'zIndex.2', children: _jsx(Box, { flexGrow: "1", position: "relative", id: "sidebar-map-container", backgroundColor: "color.background.light", children: _jsx(ErrorBoundary, { fallbackComponent: _jsx(MapErrorContent, {}), children: _jsx(MapComponent, {}) }) }) })] }) })] }));
}
